import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { usePortfolioData } from '../utils/usePortfolioData';

const About: React.FC = () => {
  const { profile, loading, error, refreshData } = usePortfolioData();

  if (loading) {
    return (
      <section id="about" className="section-padding bg-cyber-darker">
        <div className="container-custom">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block mb-4"
              >
                <RefreshCw size={32} className="text-cyber-blue" />
              </motion.div>
              <p className="text-gray-400">Loading portfolio data...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="about" className="section-padding bg-cyber-darker">
        <div className="container-custom">
          <div className="text-center">
            <p className="text-red-400 mb-4">Error loading portfolio data: {error}</p>
            <button
              onClick={refreshData}
              className="bg-cyber-blue text-white px-4 py-2 rounded-lg hover:bg-cyber-purple transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!profile) {
    return (
      <section id="about" className="section-padding bg-cyber-darker">
        <div className="container-custom">
          <div className="text-center">
            <p className="text-gray-400">No profile data available</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="section-padding bg-cyber-darker">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl font-bold gradient-text mb-6">
            About Me
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {profile.headline}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Photo and Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-64 h-64 mx-auto rounded-full bg-gradient-to-br from-cyber-blue to-cyber-purple p-1">
                <div className="w-full h-full rounded-full bg-cyber-dark flex items-center justify-center overflow-hidden">
                  {profile.profilePicture ? (
                    <img
                      src={profile.profilePicture}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-cyber-blue to-cyber-purple flex items-center justify-center">
                      <span className="text-4xl font-bold text-white">
                        {profile.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Info */}
            <div className="text-center lg:text-left space-y-6">
              <h3 className="text-2xl font-bold text-white">{profile.name}</h3>
              <p className="text-cyber-blue font-mono">{profile.location}</p>
              <p className="text-gray-300 leading-relaxed text-lg">{profile.summary}</p>
            </div>
          </motion.div>

          {/* Skills */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h3 className="text-xl font-semibold text-white mb-8">Skills & Expertise</h3>
            <div className="grid grid-cols-2 gap-6">
              {profile.skills.map((skill, index) => (
                <motion.div
                  key={skill}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-3 p-4 bg-cyber-dark rounded-lg border border-gray-800 hover:border-cyber-blue transition-colors"
                >
                  <div className="w-2 h-2 bg-cyber-blue rounded-full"></div>
                  <span className="text-gray-300 font-mono text-sm">{skill}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 